import { FC, useEffect, useMemo } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import { Grid, Typography } from '@mui/material';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledDateField from '../../../../components/controlledComponents/ControlledDateField.component';
import ControlledTypeahead from '../../../../components/controlledComponents/ControlledTypeahead.component';
import {
  BorrowerType,
  BorrowingStatus,
  ILibraryBorrowingRequest,
} from '../../../../../apis/academics/academicsLibrary.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { localStorageGet } from 'src/utils/localStorage';
import { ILoggedInUser } from 'src/apis/auth/auth.interfaces';
import {
  useAcademicsLibraryState,
  useAcademicsLibraryActions,
} from '../../contexts/academicsLibrary.context';
import { useStudentListHook } from '../../../students/hooks/useStudentListHook';
import { useStaffListHook } from '../../../staff/hooks/useStaffListHook';
import dayjs, { Dayjs } from 'dayjs';
import { useAcademicsService } from 'src/apis/academics/useAcademicsService';
import {
  isPickerDateRangeOrdered,
  zNumberFromInput,
  zOptionalNullablePickerDate,
  zOptionalNumberFromInput,
  zRequiredPickerDate,
} from 'src/utils/zodFormHelpers';

interface AddEditLibraryBorrowingRecordViewProps { }

type BorrowingRecordFormData = {
  borrowerId: string;
  borrowerType: BorrowerType;
  borrowerName: string;
  borrowerEmail?: string;
  borrowerPhone?: string;
  borrowedDate: Dayjs | null;
  dueDate: Dayjs | null;
  returnDate?: Dayjs | null;
  status: BorrowingStatus;
  renewalCount: number;
  fineAmount?: number;
  finePaid?: boolean | string;
  notes?: string;
};

const getBorrowingRecordFormSchema = (translateText: (key: string) => string) =>
  z
    .object({
    borrowerId: z.string().min(1, translateText('common.fieldRequired')),
    borrowerType: z.nativeEnum(BorrowerType, {
      required_error: translateText('common.pleaseSelectValue') || 'Please select a value here',
      invalid_type_error: translateText('common.pleaseSelectValue') || 'Please select a value here',
    }),
    borrowerName: z.string().min(1, translateText('common.fieldRequired')),
    borrowerEmail: z.string().email().optional().or(z.literal('')),
    borrowerPhone: z.string().optional(),
    borrowedDate: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    dueDate: zRequiredPickerDate({
      requiredMessage: translateText('common.fieldRequired'),
      invalidMessage: translateText('common.dateInvalid'),
    }),
    returnDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    status: z.nativeEnum(BorrowingStatus, {
      required_error: translateText('common.pleaseSelectValue') || 'Please select a value here',
      invalid_type_error: translateText('common.pleaseSelectValue') || 'Please select a value here',
    }),
    renewalCount: zNumberFromInput({ min: 0 }).default(0),
    fineAmount: zOptionalNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    finePaid: z.union([z.boolean(), z.string()]).optional(),
    notes: z.string().optional(),
  })
    .refine(
      (data) =>
        isPickerDateRangeOrdered(data.borrowedDate, data.dueDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['dueDate'],
      }
    )
    .refine(
      (data) =>
        !data.returnDate ||
        isPickerDateRangeOrdered(data.borrowedDate, data.returnDate),
      {
        message: translateText('common.endDateOnOrAfterStart'),
        path: ['returnDate'],
      }
    ) as ZodType<BorrowingRecordFormData>;

const getBorrowerTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: BorrowerType.STUDENT,
      value:
        translateText('academics.library.borrowerTypes.student') || 'Student',
    },
    {
      key: BorrowerType.STAFF,
      value: translateText('academics.library.borrowerTypes.staff') || 'Staff',
    },
    {
      key: BorrowerType.PARENT,
      value: translateText('academics.library.borrowerTypes.parent') || 'Parent',
    },
    {
      key: BorrowerType.VISITOR,
      value:
        translateText('academics.library.borrowerTypes.visitor') || 'Visitor',
    },
  ];

const getBorrowingStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: BorrowingStatus.AVAILABLE,
      value: translateText('academics.library.statuses.available') || 'Available',
    },
    {
      key: BorrowingStatus.BORROWED,
      value: translateText('academics.library.statuses.borrowed') || 'Borrowed',
    },
    {
      key: BorrowingStatus.RESERVED,
      value: translateText('academics.library.statuses.reserved') || 'Reserved',
    },
    {
      key: BorrowingStatus.OVERDUE,
      value: translateText('academics.library.statuses.overdue') || 'Overdue',
    },
    {
      key: BorrowingStatus.LOST,
      value: translateText('academics.library.statuses.lost') || 'Lost',
    },
    {
      key: BorrowingStatus.DAMAGED,
      value: translateText('academics.library.statuses.damaged') || 'Damaged',
    },
    {
      key: BorrowingStatus.MAINTENANCE,
      value:
        translateText('academics.library.statuses.maintenance') || 'Maintenance',
    },
    {
      key: BorrowingStatus.RETIRED,
      value: translateText('academics.library.statuses.retired') || 'Retired',
    },
  ];

const AddEditLibraryBorrowingRecordView: FC<
  AddEditLibraryBorrowingRecordViewProps
> = () => {
  const { translateText } = useTranslation();
  const borrowingRecordFormSchema = getBorrowingRecordFormSchema(translateText);

  const {
    academicsLibraryAppState: {
      isManageBorrowingRecordOpen,
      selectedBorrowingRecord,
      selectedLibraryItemIdForBorrowing,
      isCreateBorrowingRecordMode,
      isEditBorrowingRecordMode,
      isBorrowingRecordLoading,
      borrowingRecordErrorMessage,
    },
  } = useAcademicsLibraryState();

  const {
    setIsManageBorrowingRecordOpen,
    setIsBorrowingRecordLoading,
    setIsBorrowingRecordError,
    setBorrowingRecordErrorMessage,
  } = useAcademicsLibraryActions();

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const instituteId = (userFromStorage as any)?.instituteId || '';

  const {
    createAcademicsLibraryBorrowingRecord,
    patchAcademicsLibraryBorrowingRecord,
  } = useAcademicsService();

  const { data: studentListResponse } = useStudentListHook({
    page: 1,
    limit: 1000,
  });

  const { data: staffListResponse } = useStaffListHook({
    page: 1,
    limit: 1000,
  });

  const studentOptions = useMemo<TKeyValue<string>[]>(() => {
    if (!studentListResponse?.data) return [];
    return studentListResponse.data.map((student) => ({
      key: student.id,
      value:
        `${student.basicDetails.firstName} ${student.basicDetails.middleName || ''} ${student.basicDetails.lastName}`.trim(),
    }));
  }, [studentListResponse]);

  const staffOptions = useMemo<TKeyValue<string>[]>(() => {
    if (!staffListResponse?.data) return [];
    return staffListResponse.data.map((staff) => ({
      key: staff.id,
      value:
        `${staff.basicDetails.firstName} ${staff.basicDetails.middleName || ''} ${staff.basicDetails.lastName}`.trim(),
    }));
  }, [staffListResponse]);

  const defaultValues: BorrowingRecordFormData = {
    borrowerId: '',
    borrowerType: BorrowerType.STUDENT,
    borrowerName: '',
    borrowerEmail: '',
    borrowerPhone: '',
    borrowedDate: dayjs(),
    dueDate: dayjs().add(7, 'day'),
    returnDate: null,
    status: BorrowingStatus.BORROWED,
    renewalCount: 0,
    fineAmount: undefined,
    finePaid: 'false',
    notes: '',
  };

  const methods = useForm<BorrowingRecordFormData>({
    resolver: zodResolver(borrowingRecordFormSchema),
    mode: 'all',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    trigger,
  } = methods;

  const watchedBorrowerType = watch('borrowerType');
  const watchedBorrowerId = watch('borrowerId');

  const borrowerOptions = useMemo<TKeyValue<string>[]>(() => {
    if (watchedBorrowerType === BorrowerType.STUDENT) {
      return studentOptions;
    } else if (watchedBorrowerType === BorrowerType.STAFF) {
      return staffOptions;
    }
    // For PARENT and VISITOR types, return empty array as they need manual entry
    return [];
  }, [watchedBorrowerType, studentOptions, staffOptions]);

  // Auto-populate borrower name and email when borrower is selected
  useEffect(() => {
    if (
      watchedBorrowerId &&
      (watchedBorrowerType === BorrowerType.STUDENT ||
        watchedBorrowerType === BorrowerType.STAFF)
    ) {
      const selectedBorrower =
        watchedBorrowerType === BorrowerType.STUDENT
          ? studentListResponse?.data.find((s) => s.id === watchedBorrowerId)
          : staffListResponse?.data.find((s) => s.id === watchedBorrowerId);

      if (selectedBorrower) {
        const fullName =
          `${selectedBorrower.basicDetails.firstName} ${selectedBorrower.basicDetails.middleName || ''} ${selectedBorrower.basicDetails.lastName}`.trim();
        methods.setValue('borrowerName', fullName);
        if (selectedBorrower.contactDetails?.email?.email) {
          methods.setValue(
            'borrowerEmail',
            selectedBorrower.contactDetails.email.email
          );
        }
        if (selectedBorrower.contactDetails?.phone?.phoneNumber) {
          const phoneNumber =
            `${selectedBorrower.contactDetails.phone.countryCode || ''} ${selectedBorrower.contactDetails.phone.phoneNumber}`.trim();
          methods.setValue('borrowerPhone', phoneNumber);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    watchedBorrowerId,
    watchedBorrowerType,
    studentListResponse,
    staffListResponse,
  ]);

  useEffect(() => {
    if (isManageBorrowingRecordOpen) {
      // Clear any previous error messages when modal opens
      setIsBorrowingRecordError(false);
      setBorrowingRecordErrorMessage('');
    }

    if (selectedBorrowingRecord && isManageBorrowingRecordOpen) {
      reset({
        borrowerId: selectedBorrowingRecord.borrowerId || '',
        borrowerType: selectedBorrowingRecord.borrowerType,
        borrowerName: selectedBorrowingRecord.borrowerName || '',
        borrowerEmail: selectedBorrowingRecord.borrowerEmail || '',
        borrowerPhone: selectedBorrowingRecord.borrowerPhone || '',
        borrowedDate: selectedBorrowingRecord.borrowedDate
          ? dayjs(selectedBorrowingRecord.borrowedDate)
          : null,
        dueDate: selectedBorrowingRecord.dueDate
          ? dayjs(selectedBorrowingRecord.dueDate)
          : null,
        returnDate: selectedBorrowingRecord.returnDate
          ? dayjs(selectedBorrowingRecord.returnDate)
          : null,
        status: selectedBorrowingRecord.status,
        renewalCount: selectedBorrowingRecord.renewalCount || 0,
        fineAmount: selectedBorrowingRecord.fineAmount,
        finePaid: selectedBorrowingRecord.finePaid
          ? String(selectedBorrowingRecord.finePaid)
          : 'false',
        notes: selectedBorrowingRecord.notes || '',
      });
    } else if (isManageBorrowingRecordOpen && isCreateBorrowingRecordMode) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedBorrowingRecord,
    isManageBorrowingRecordOpen,
    isCreateBorrowingRecordMode,
    reset,
  ]);

  const onSubmitForm = async (data: BorrowingRecordFormData) => {
    const borrowedDateStr = data.borrowedDate
      ? data.borrowedDate.format('YYYY-MM-DD')
      : new Date().toISOString().split('T')[0];
    const dueDateStr = data.dueDate ? data.dueDate.format('YYYY-MM-DD') : '';
    const returnDateStr = data.returnDate
      ? data.returnDate.format('YYYY-MM-DD')
      : undefined;

    // Convert finePaid from string to boolean if needed
    if (!selectedLibraryItemIdForBorrowing) {
      setBorrowingRecordErrorMessage(
        translateText('academics.library.itemRequired') ||
        'Library item is required'
      );
      setIsBorrowingRecordError(true);
      return;
    }

    if (!instituteId) {
      setBorrowingRecordErrorMessage(
        translateText('common.fieldRequired') || 'Institute ID is required'
      );
      setIsBorrowingRecordError(true);
      return;
    }

    const borrowingRequest: ILibraryBorrowingRequest = {
      borrowingRecord: {
        instituteId,
        inventoryId: selectedLibraryItemIdForBorrowing,
        borrowerId: data.borrowerId,
        borrowedDate: borrowedDateStr,
        dueDate: dueDateStr,
        notes: data.notes || undefined,
        status: data.status as any,
        returnedDate: returnDateStr,
      },
    };

    setIsBorrowingRecordLoading(true);
    setIsBorrowingRecordError(false);
    setBorrowingRecordErrorMessage('');

    try {
      if (isEditBorrowingRecordMode && selectedBorrowingRecord?.recordId) {
        await patchAcademicsLibraryBorrowingRecord(
          selectedBorrowingRecord.recordId,
          borrowingRequest
        );
      } else {
        await createAcademicsLibraryBorrowingRecord(borrowingRequest);
      }

      setIsManageBorrowingRecordOpen(false);
    } catch (error: any) {
      setIsBorrowingRecordError(true);
      setBorrowingRecordErrorMessage(
        error?.message || translateText('common.error') || 'An error occurred'
      );
    } finally {
      setIsBorrowingRecordLoading(false);
    }
  };

  const handleClose = () => {
    setIsManageBorrowingRecordOpen(false);
    setIsBorrowingRecordError(false);
    setBorrowingRecordErrorMessage('');
    reset(defaultValues);
  };

  const onValidationError = () => {
    // Clear footer error message when validation fails - errors should show on fields
    setIsBorrowingRecordError(false);
    setBorrowingRecordErrorMessage('');
    // Trigger validation for all fields to ensure errors are displayed
    trigger();
  };

  return (
    <div data-testid="add-edit-borrowing-record-container">
      <StructuredModal
        isOpen={isManageBorrowingRecordOpen}
        onClose={handleClose}
        title={
          isEditBorrowingRecordMode
            ? translateText('academics.library.editBorrowingRecord') ||
            'Edit Borrowing Record'
            : translateText('academics.library.addBorrowingRecord') ||
            'Add Borrowing Record'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm, onValidationError)}
        footerPositiveActionText={
          isEditBorrowingRecordMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={isBorrowingRecordLoading}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          borrowingRecordErrorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="borrowing-record-error-message"
            >
              {borrowingRecordErrorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm, onValidationError)}
            style={{ width: '100%' }}
            data-testid="borrowing-record-form"
          >
            <Grid
              container
              spacing={{ xs: 2, sm: 4 }}
              padding={{ xs: 1, sm: 2 }}
              data-testid="borrowing-record-form-container"
            >
              <Grid item xs={12} data-testid="borrowing-record-details-section">
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {translateText('academics.library.borrowingDetails') ||
                    'Borrowing Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="borrower-type-field"
              >
                <ControlledSelectField
                  control={control}
                  name="borrowerType"
                  id="borrower-type"
                  label={
                    translateText('academics.library.borrowerType') ||
                    'Borrower Type'
                  }
                  itemList={getBorrowerTypeOptions(translateText)}
                  errorMessage={errors.borrowerType?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} data-testid="borrower-field">
                {borrowerOptions.length > 0 ? (
                  <ControlledTypeahead
                    control={control}
                    name="borrowerId"
                    id="borrower-id"
                    label={
                      translateText('academics.library.borrower') || 'Borrower'
                    }
                    options={borrowerOptions}
                    errorMessage={errors.borrowerId?.message}
                    isRequired
                    isGridItem={false}
                  />
                ) : (
                  <ControlledTextField
                    control={control}
                    name="borrowerId"
                    id="borrower-id"
                    label={
                      translateText('academics.library.borrower') || 'Borrower'
                    }
                    errorMessage={errors.borrowerId?.message}
                    isGridItem={false}
                    fullWidth
                  />
                )}
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="borrower-name-field"
              >
                <ControlledTextField
                  control={control}
                  name="borrowerName"
                  id="borrower-name"
                  label={
                    translateText('academics.library.borrowerName') ||
                    'Borrower Name'
                  }
                  errorMessage={errors.borrowerName?.message}
                  isGridItem={false}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="borrower-email-field"
              >
                <ControlledTextField
                  control={control}
                  name="borrowerEmail"
                  id="borrower-email"
                  label={
                    translateText('academics.library.borrowerEmail') ||
                    'Borrower Email'
                  }
                  errorMessage={errors.borrowerEmail?.message}
                  isGridItem={false}
                  fullWidth
                  type="email"
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="borrower-phone-field"
              >
                <ControlledTextField
                  control={control}
                  name="borrowerPhone"
                  id="borrower-phone"
                  label={
                    translateText('academics.library.borrowerPhone') ||
                    'Borrower Phone'
                  }
                  errorMessage={errors.borrowerPhone?.message}
                  isGridItem={false}
                  fullWidth
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="borrowed-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="borrowedDate"
                  id="borrowed-date"
                  label={
                    translateText('academics.library.borrowedDate') ||
                    'Borrowed Date'
                  }
                  errorMessage={errors.borrowedDate?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} data-testid="due-date-field">
                <ControlledDateField
                  control={control}
                  name="dueDate"
                  id="due-date"
                  label={
                    translateText('academics.library.dueDate') || 'Due Date'
                  }
                  errorMessage={errors.dueDate?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              {isEditBorrowingRecordMode && (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  data-testid="return-date-field"
                >
                  <ControlledDateField
                    control={control}
                    name="returnDate"
                    id="return-date"
                    label={
                      translateText('academics.library.returnDate') ||
                      'Return Date'
                    }
                    errorMessage={errors.returnDate?.message}
                    isGridItem={false}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={6} data-testid="status-field">
                <ControlledSelectField
                  control={control}
                  name="status"
                  id="status"
                  label={translateText('academics.library.status') || 'Status'}
                  itemList={getBorrowingStatusOptions(translateText)}
                  errorMessage={errors.status?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="renewal-count-field"
              >
                <ControlledTextField
                  control={control}
                  name="renewalCount"
                  id="renewal-count"
                  label={
                    translateText('academics.library.renewalCount') ||
                    'Renewal Count'
                  }
                  errorMessage={errors.renewalCount?.message}
                  isGridItem={false}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} data-testid="fine-amount-field">
                <ControlledTextField
                  control={control}
                  name="fineAmount"
                  id="fine-amount"
                  label={
                    translateText('academics.library.fineAmount') ||
                    'Fine Amount'
                  }
                  errorMessage={errors.fineAmount?.message}
                  isGridItem={false}
                  fullWidth
                  type="number"
                  inputProps={{ min: 0 }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={6} data-testid="fine-paid-field">
                <ControlledSelectField
                  control={control}
                  name="finePaid"
                  id="fine-paid"
                  label={
                    translateText('academics.library.finePaid') || 'Fine Paid'
                  }
                  itemList={[
                    {
                      key: 'true',
                      value: translateText('common.yes') || 'Yes',
                    },
                    { key: 'false', value: translateText('common.no') || 'No' },
                  ]}
                  errorMessage={errors.finePaid?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="notes-field">
                <ControlledTextField
                  control={control}
                  name="notes"
                  id="notes"
                  label={translateText('common.remarks') || 'Remarks'}
                  errorMessage={errors.notes?.message}
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditLibraryBorrowingRecordView;
